import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Repeat, X } from 'lucide-react'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { contactSnapshot } from '@/lib/contactSnapshot'
import type { Frequency } from '@/lib/recurringInvoiceCadence'
import CadencePicker, { type EndCondition } from '@/components/payments/CadencePicker'

/**
 * From-scratch "New schedule" modal — the third surface using <CadencePicker>
 * (after MakeRecurringModal create-from-invoice and RecurringInvoiceDetail edit).
 *
 * Create-then-navigate flow mirroring NewInvoiceModal:
 *   1. Operator picks customer + cadence here.
 *   2. INSERT a series with an EMPTY template (line_items: []).
 *   3. Parent navigates to /app/invoices/recurring/:id where the LineItemEditor
 *      + DiscountRow + tax + payment_terms + notes editor (RecurringInvoiceDetail)
 *      accept the actual billing details.
 *
 * CRITICAL: NO manual-name fallback. The only path to set contact_id is
 * picking a real contact from the dropdown — the series's NOT-NULL FK to
 * contacts(id) rejects the sentinel UUID that NewInvoiceModal writes. This
 * modal cannot produce the sentinel by construction (see commit 32cd68a).
 *
 * Title auto-default differs from MakeRecurringModal: contact isn't known
 * at mount, so the default fires on TWO events (frequency change AND contact
 * pick) gated by the same titleTouched flag.
 */

type ContactRow = {
  id: string
  first_name: string
  last_name?: string | null
  email?: string | null
  company_name?: string | null
}

const FREQUENCY_LABELS: Record<Frequency, string> = {
  weekly: 'Weekly', monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Yearly',
}

function defaultTitleFor(freq: Frequency, contactName: string): string {
  return contactName ? `${contactName} — ${FREQUENCY_LABELS[freq]}` : ''
}

interface Props {
  onClose: () => void
  onCreated: (seriesId: string) => void
  contacts: ContactRow[]
}

export default function NewRecurringScheduleModal({ onClose, onCreated, contacts }: Props) {
  const { workspace } = useAuth()
  const today = new Date().toISOString().split('T')[0]

  const [form, setForm] = useState({
    contact_id: '',
    contact_name: '',
    contact_email: '',
    company_name: '',
  })
  const [title, setTitle] = useState('')
  const [titleTouched, setTitleTouched] = useState(false)
  const [frequency, setFrequency] = useState<Frequency>('monthly')
  const [startDate, setStartDate] = useState(today)
  const [endCondition, setEndCondition] = useState<EndCondition>('never')
  const [endDate, setEndDate] = useState('')
  const [endAfterCount, setEndAfterCount] = useState('12')
  const [sendMode, setSendMode] = useState<'draft' | 'auto_send'>('draft')
  const [saving, setSaving] = useState(false)

  const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }
  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }

  const handleContactSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const contact = contacts.find(c => c.id === e.target.value)
    if (!contact) {
      // Reset to empty selection (the placeholder option).
      setForm({ contact_id: '', contact_name: '', contact_email: '', company_name: '' })
      return
    }
    const snap = contactSnapshot(contact)
    setForm({
      contact_id: contact.id,
      contact_name: snap.contact_name,
      contact_email: contact.email || '',
      company_name: snap.company_name ?? '',
    })
    // Auto-default the title once a contact is picked (only if the operator
    // hasn't typed their own value).
    if (!titleTouched) setTitle(defaultTitleFor(frequency, snap.contact_name))
  }

  const onFrequencyChange = (next: Frequency) => {
    setFrequency(next)
    if (!titleTouched) setTitle(defaultTitleFor(next, form.contact_name))
  }

  const startIsToday = startDate === today
  const startIsPast  = startDate !== '' && startDate < today

  const handleSubmit = async () => {
    // Contact is THE key check. The series FK rejects anything that isn't a
    // real contacts(id) — by construction this modal can only set contact_id
    // from the dropdown, so a missing pick is the only failure mode here.
    if (!form.contact_id) { toast.error('Please select a customer'); return }
    if (!title.trim())   { toast.error('Schedule needs a title'); return }
    if (!startDate)      { toast.error('Pick a start date'); return }
    if (endCondition === 'on_date' && !endDate) { toast.error('Pick an end date'); return }
    if (endCondition === 'after_count') {
      const n = parseInt(endAfterCount, 10)
      if (!Number.isFinite(n) || n < 1) { toast.error('After count must be at least 1'); return }
    }

    // Empty template — operator fills line_items / discount / tax / notes on
    // the detail page after this insert. Defaults match MakeRecurringModal's
    // template build so the generated invoices behave identically once the
    // operator populates the rest.
    const template: Record<string, any> = {
      line_items: [],
      contact_name: form.contact_name,
      contact_email: form.contact_email,
      company_name: form.company_name || null,
      payment_terms: 'net_30',
      discount_type: 'none',
      discount_value: 0,
      tax_rate: 0,
      notes: null,
      footer_text: null,
      service_line_id: null,
      currency: 'USD',
    }

    setSaving(true)
    const { data, error } = await supabase
      .from('recurring_invoice_series')
      .insert({
        workspace_id: workspace!.id,
        contact_id: form.contact_id,            // real id by construction
        company_id: null,                        // contacts query doesn't expose company_id; denormalized in template only (banked)
        service_line_id: null,
        title: title.trim(),
        template,
        frequency,
        start_date: startDate,
        next_run_date: startDate,                // first generation = start_date
        end_date: endCondition === 'on_date' ? endDate : null,
        end_after_count: endCondition === 'after_count' ? parseInt(endAfterCount, 10) : null,
        send_mode: sendMode,
        status: 'active',
        generated_count: 0,
      })
      .select('id')
      .single()
    setSaving(false)

    if (error) { toast.error(`Couldn't create schedule: ${error.message}`); return }
    toast.success('Schedule created — add your line items')
    onCreated(data!.id)
    onClose()
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}
      onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.15 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 20, padding: 28, width: '100%', maxWidth: 520, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Repeat style={{ width: 16, height: 16, color: 'var(--accent)' }} />
            <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>New recurring schedule</h2>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 18, height: 18 }} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto', minHeight: 0 }}>
          <div style={{ padding: '10px 12px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 9, fontSize: 12, color: 'var(--ink-secondary)' }}>
            Set up the cadence and customer here. You'll add line items on the next screen.
          </div>

          <div>
            <label style={lbl}>Customer *</label>
            <select value={form.contact_id} onChange={handleContactSelect}
              style={{ ...inp, cursor: 'pointer' }}>
              <option value="">Select a customer…</option>
              {contacts.map(c => (
                <option key={c.id} value={c.id}>
                  {c.first_name} {c.last_name || ''}{c.company_name ? ` — ${c.company_name}` : ''}
                </option>
              ))}
            </select>
            {contacts.length === 0 && (
              <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '6px 0 0' }}>
                No contacts in this workspace yet. Add a contact in Contacts first.
              </p>
            )}
          </div>

          <div>
            <label style={lbl}>Schedule title</label>
            <input value={title}
              onChange={e => { setTitle(e.target.value); setTitleTouched(true) }}
              placeholder={form.contact_name ? defaultTitleFor(frequency, form.contact_name) : 'Pick a customer to suggest a title'}
              style={inp}
              onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
          </div>

          <CadencePicker
            frequency={frequency}
            startDate={startDate}
            endCondition={endCondition}
            endDate={endDate}
            endAfterCount={endAfterCount}
            sendMode={sendMode}
            onFrequencyChange={onFrequencyChange}
            onStartDateChange={setStartDate}
            onEndConditionChange={setEndCondition}
            onEndDateChange={setEndDate}
            onEndAfterCountChange={setEndAfterCount}
            onSendModeChange={setSendMode}
            previewHeader="first"
            showSweepNote={startIsToday || startIsPast}
          />
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 20, flexShrink: 0 }}>
          <button onClick={onClose} className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
          <button onClick={handleSubmit} disabled={saving} className="btn-primary" style={{ flex: 1, justifyContent: 'center' }}>
            {saving ? 'Creating...' : 'Create schedule'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}
